import { useSelector, useDispatch, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    useGetOrganizationOtherInfoQuery,
    useUpdateOrganizationProfileMutation,
} from "Services/OrganizationProfileService";
import { useGetLetterHead2Query } from "Services/LetterHeadService";
import { showSuccessToasterBottomLeft, showErrorToaster } from "store/Shared.Slice";
import { handleModelErrors } from "views/account/organization-profile/OrganizationProfileInfo.Slice";
import { selectOtherInfoLoaded, setErrorFields } from "views/shared/organization-profile-stepper/slice";

export default function useOrganizationProfileStepper({ enabled = true } = {}) {
    const dispatch = useDispatch();
    const store = useStore();
    const { t } = useTranslation();
    const otherInfoLoaded = useSelector(selectOtherInfoLoaded);

    useGetOrganizationOtherInfoQuery(null, { refetchOnMountOrArgChange: true, skip: !enabled });
    useGetLetterHead2Query("organization", { refetchOnMountOrArgChange: true, skip: !enabled });

    const [updateOrganizationProfile] = useUpdateOrganizationProfileMutation();

    const submitUpdate = async (addedAreas) => {
        // Confirmation may resume a callback captured before the latest render.
        const { organizationStepperState: otherOrganization, organizationProfileState: profileInfo } = store.getState();
        // The stepper submits the WHOLE profile (all steps combined) on every Save/Next click.
        // Until getOrganizationOtherInfo has resolved at least once, otherOrganization still holds
        // the slice's empty defaults, so saving now would overwrite the org's real saved data
        // (categories, populations served, geo areas, SDG goals, similar charities) with [].
        if (!selectOtherInfoLoaded(store.getState())) {
            dispatch(showErrorToaster(t("errors.profile_still_loading")));
            return false;
        }

        const socialMediaType = {
            facebook: 0,
            linkedin: 1,
            instagram: 2,
            x: 3,
        };

        const socialMedia = Object.entries(otherOrganization.links)
            .filter(([key]) => key !== "website")
            .map(([key, value]) => ({
                socialMediaType: socialMediaType[key],
                website: value,
            }));

        const similarCharities = otherOrganization.similarCharities.map((charity) => ({
            ein: charity.ein,
            name: charity.label,
            city: charity.city,
            state: charity.state,
            county: charity.county,
            nteeCode: charity.nteeCode,
            nteeTitle: charity.nteeTitle,
        }));

        // businessEin is only editable in the "other information" stepper (BusinessNumberStep) —
        // the profile-info tab renders it disabled, so it must not be used as the source here.
        const companyInfoModel = {
            companyName: profileInfo.companyName.value,
            phoneNumber: profileInfo.phoneNumber.value,
            businessEin: otherOrganization.bnein,
            addressLine: profileInfo.addressLine.value,
            addressLine2: profileInfo.addressLine2.value,
            city: profileInfo.city.value,
            provinceOrState: profileInfo.provinceOrState.value,
            county: profileInfo.county.value,
            postalCode: profileInfo.postalCode.value,
            website: otherOrganization.links.website,
            socialMedia,
            mission: otherOrganization.missionStatement,
            categories: otherOrganization.categories,
            populationsServed: otherOrganization.populationsServed,
            geographicAreasServed: (Array.isArray(addedAreas) ? addedAreas : otherOrganization.gasServed).map(area => JSON.stringify(area)),
            unSdgGoals: otherOrganization.sdgServed,
            similarCharities,
        };

        let hasError = false;

        try {
            await updateOrganizationProfile(companyInfoModel).unwrap();

        } catch (error) {
            hasError = true;
            const errors = error?.data?.errors;
            if (errors != null) {
                dispatch(setErrorFields(errors));
                dispatch(showErrorToaster(t("errors.model_error")));
                dispatch(handleModelErrors(errors));
            } else if (error?.data?.message != null) {
                dispatch(showErrorToaster(error.data.message));
            }
        }

        if (!hasError) {
            dispatch(showSuccessToasterBottomLeft(t("success.organization_update")));
        }

        return !hasError;
    };

    return { submitUpdate, isReady: otherInfoLoaded };
}
